import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import { TerminationModal_MembershipTermDataFragment } from './graphql-types/TerminationModal_MembershipTermDataFragment';
import {
  TerminationModal_TerminateMembershipTerm,
  TerminationModal_TerminateMembershipTermVariables,
} from './graphql-types/TerminationModal_TerminateMembershipTerm';

const TERMINATE_MEMBERSHIP_TERM = gql`
  mutation TerminationModal_TerminateMembershipTerm(
    $input: TerminateMembershipTermInput!
  ) {
    terminateMembershipTerm(input: $input) {
      ... on TerminateMembershipTermSuccess {
        membershipTerm {
          _id
          importantDates {
            date
          }
        }
      }
    }
  }
`;

export const TerminationModal: React.FC<{
  membershipTerm: TerminationModal_MembershipTermDataFragment;
  displayName: string;
  onClose: () => void;
  isOpen: boolean;
  hasNextTerm: boolean;
}> & { membershipTermDataFragment: DocumentNode } = ({
  membershipTerm,
  displayName,
  onClose,
  isOpen,
  hasNextTerm,
}) => {
  const toast = useToast();

  const [terminateMembershipTerm] = useMutation<
    TerminationModal_TerminateMembershipTerm,
    TerminationModal_TerminateMembershipTermVariables
  >(TERMINATE_MEMBERSHIP_TERM, {
    refetchQueries: ['HistoryCard_User'],
  });

  const terminateMembershipTermAsync = async (): Promise<void> => {
    try {
      const terminateMembershipTermResult = await terminateMembershipTerm({
        variables: {
          input: {
            membershipTermId: membershipTerm._id,
          },
        },
      });

      if (
        terminateMembershipTermResult.data?.terminateMembershipTerm
          .__typename === 'TerminateMembershipTermSuccess'
      ) {
        toast({
          title: 'Membership was successfully terminated',
          status: 'success',
        });
      } else {
        toast({
          title: 'An unknown error occurred',
          status: 'error',
        });
      }
    } catch {
      toast({
        title: 'An unknown error occurred',
        status: 'error',
      });
    }
  };

  const dialogBody = `Terminating this membership means ${displayName} will lose membership benefits immediately${
    !!hasNextTerm ? ' and the upcoming membership will be cancelled' : ''
  }. This action cannot be undone.`;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Are you sure you want to terminate this membership?"
      body={dialogBody}
      confirmButtonText="Continue"
      onConfirm={terminateMembershipTermAsync}
    />
  );
};

TerminationModal.membershipTermDataFragment = gql`
  fragment TerminationModal_MembershipTermDataFragment on MembershipTermV2 {
    _id
  }
`;
